import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useAtom } from 'jotai';
import { FC, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  LOCATIONS_DASHBOARD_BANNER_VERSION,
  locationsDashboardBannerDismissalsAtom,
} from 'src/atoms/ui/locationsBanner';
import { useAuth } from 'src/auth/hooks/useAuth';

export const LocationsDashboardBanner: FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [dismissals, setDismissals] = useAtom(locationsDashboardBannerDismissalsAtom);

  const dismissedByCurrentUser = useMemo(() => {
    const dismissedVersions = dismissals[user.emailAddress] ?? [];
    return dismissedVersions.includes(LOCATIONS_DASHBOARD_BANNER_VERSION);
  }, [dismissals, user.emailAddress]);

  const dismissBanner = () => {
    setDismissals((prev) => {
      const dismissedVersions = prev[user.emailAddress] ?? [];
      if (dismissedVersions.includes(LOCATIONS_DASHBOARD_BANNER_VERSION)) {
        return prev;
      }

      return {
        ...prev,
        [user.emailAddress]: [...dismissedVersions, LOCATIONS_DASHBOARD_BANNER_VERSION],
      };
    });
  };

  if (dismissedByCurrentUser) {
    return null;
  }

  return (
    <Alert
      data-testid="locations.dashboardBanner"
      severity="info"
      icon={<InfoOutlinedIcon fontSize="small" />}
      action={
        <IconButton
          data-testid="locations.dashboardBanner.dismiss"
          aria-label={t('locations.banner.dismissAriaLabel')}
          size="small"
          onClick={dismissBanner}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      }
      sx={(theme) => ({
        backgroundColor: theme.palette.blue[100],
        color: theme.palette.text.secondary,
        border: `1px solid ${theme.palette.blue[200]}`,
        '& .MuiAlert-icon': {
          color: theme.palette.blue[600],
        },
        alignItems: 'center',
      })}
    >
      <Typography variant="text1" color="text.secondary">
        <Trans
          i18nKey="locations.banner.message"
          components={{
            supportLink: (
              <Link
                href="https://cslgroup.atlassian.net/jira/software/c/projects/IOT/boards/1425/backlog"
                target="_blank"
                rel="noopener noreferrer"
                underline="always"
              />
            ),
          }}
        />
      </Typography>
    </Alert>
  );
};
